import { customFetch } from '@/lib/api/fetcher'

/**
 * References API client — hand-rolled wrappers over `customFetch` so we don't
 * depend on orval regeneration for the new endpoints. Each shape mirrors the
 * backend Pydantic schema exactly.
 */

// ============ Units of measure ============

export interface UnitOut {
  id: number
  code: string
  name: string
  short_name: string
  is_active: boolean
}

export interface UnitCreate {
  name: string
  short_name?: string
}

export interface UnitUpdate {
  code?: string
  name?: string
  short_name?: string
  is_active?: boolean
}

export const unitsApi = {
  list: (signal?: AbortSignal) =>
    customFetch<UnitOut[]>('/api/units-of-measure/?is_active=false', { signal }),
  create: (payload: UnitCreate) =>
    customFetch<UnitOut>('/api/units-of-measure/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: UnitUpdate) =>
    customFetch<UnitOut>(`/api/units-of-measure/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  remove: (id: number) => customFetch<void>(`/api/units-of-measure/${id}`, { method: 'DELETE' }),
}

// ============ Operations ============

export interface OperationOut {
  id: number
  code: string
  name: string
  work_order_types: string[]
  is_active: boolean
}

export interface OperationCreate {
  code: string
  name: string
  work_order_types: string[]
}

export interface OperationUpdate {
  code?: string
  name?: string
  work_order_types?: string[]
  is_active?: boolean
}

export const operationsApi = {
  list: (signal?: AbortSignal) =>
    customFetch<OperationOut[]>('/api/operations?include_inactive=true', { signal }),
  create: (payload: OperationCreate) =>
    customFetch<OperationOut>('/api/operations', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: OperationUpdate) =>
    customFetch<OperationOut>(`/api/operations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  remove: (id: number) => customFetch<void>(`/api/operations/${id}`, { method: 'DELETE' }),
}

// ============ Positions ============

export interface PositionOut {
  id: number
  code: string
  name: string
  is_active: boolean
}

export interface PositionCreate {
  code: string
  name: string
}

export interface PositionUpdate {
  code?: string
  name?: string
  is_active?: boolean
}

export const positionsApi = {
  list: (signal?: AbortSignal) =>
    customFetch<PositionOut[]>('/api/positions?include_inactive=true', { signal }),
  create: (payload: PositionCreate) =>
    customFetch<PositionOut>('/api/positions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: PositionUpdate) =>
    customFetch<PositionOut>(`/api/positions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  remove: (id: number) => customFetch<void>(`/api/positions/${id}`, { method: 'DELETE' }),
}

// ============ Users ============

export interface UserOutFull {
  id: number
  tab_no: string
  full_name: string
  first_name: string
  last_name: string
  patronymic: string
  email: string | null
  is_active: boolean
  is_staff: boolean
  position_id: number | null
  position_code: string | null
  position_name: string | null
}

export interface UserCreate {
  tab_no: string
  first_name?: string
  last_name?: string
  patronymic?: string
  position_id?: number | null
}

export interface UserUpdate {
  tab_no?: string
  first_name?: string
  last_name?: string
  patronymic?: string
  position_id?: number | null
  is_active?: boolean
}

export const usersApi = {
  list: (signal?: AbortSignal) =>
    customFetch<UserOutFull[]>('/api/users/?include_archived=true', { signal }),
  create: (payload: UserCreate) =>
    customFetch<UserOutFull>('/api/users/', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: UserUpdate) =>
    customFetch<UserOutFull>(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
}

// ============ Warehouses ============

export interface WarehouseOut {
  id: number
  code: string
  name: string
  responsible_id: number
  notes: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface WarehouseCreate {
  code: string
  name: string
  responsible_id: number
  notes?: string
  is_active?: boolean
}

export interface WarehouseUpdate {
  code?: string
  name?: string
  responsible_id?: number
  notes?: string
  is_active?: boolean
}

export const warehousesApi = {
  list: (signal?: AbortSignal) =>
    customFetch<WarehouseOut[]>('/api/catalog/warehouses', { signal }),
  get: (id: number, signal?: AbortSignal) =>
    customFetch<WarehouseOut>(`/api/catalog/warehouses/${id}`, { signal }),
  create: (payload: WarehouseCreate) =>
    customFetch<WarehouseOut>('/api/catalog/warehouses', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: WarehouseUpdate) =>
    customFetch<WarehouseOut>(`/api/catalog/warehouses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  remove: (id: number) => customFetch<void>(`/api/catalog/warehouses/${id}`, { method: 'DELETE' }),
}

// ============ Query keys ============

export const refKeys = {
  units: () => ['references', 'units'] as const,
  operations: () => ['references', 'operations'] as const,
  positions: () => ['references', 'positions'] as const,
  users: () => ['references', 'users'] as const,
  warehouses: () => ['references', 'warehouses'] as const,
  warehouse: (id: number) => ['references', 'warehouses', id] as const,
}
